<template>
    <div v-show="isOpen" class="remote-console-2__background">
        <div
            class="remote-console-2__modal--wrapper"
            :class="{
                'remote-console-2__modal--wrapper--mobile': isMobile || isTablet,
            }"
            @click="handleOutsideClick"
        >
            <div ref="modal" class="remote-console-2__modal">
                <div id="qtwrapper" class="remote-console-2__canvas--wrapper">
                    <div id="qtcanvas"></div>
                    <div id="qtspinner" class="remote-console-2__centered-content">
                        <div class="remote-console-2__content">
                            <img src="/beta-gui/victron-white.svg" />
                            <div id="rc2loading" class="remote-console-2__loading-state">
                                <img
                                    src="/img/loading.gif"
                                    class="remote-console-2__loading-spinner"
                                />
                                <div id="qtstatus">
                                    {{ downloadingText }}
                                </div>
                            </div>
                            <div
                                id="rc2error"
                                class="remote-console-status-content remote-console-2__error"
                            >
                                <div class="alarm">
                                    <span
                                        class="remote-console-control-icon sg-vrm-icon_012_alarm-32px"
                                    />
                                    <div id="errstatus"></div>
                                </div>
                            </div>
                            <noscript>
                                JavaScript is disabled. Please enable JavaScript to use this
                                application.
                            </noscript>
                        </div>
                    </div>
                </div>
                <span
                    class="vrm-modal__close-handle sg-vrm-icon_060_cross-round-24px"
                    @click="handleClose"
                />
            </div>
        </div>
    </div>
</template>
<script>
import { Vue, Prop, Watch, Component } from 'vue-property-decorator';
import { AngularDirective, ngInject } from '@/vue/decorators/angular-interop';
import Bowser from 'bowser';
import { _QtLoader } from '@/angular/libs/venus-beta/qtloader';
import { NOTIFICATION_TYPE_ERROR } from '@/vue/store/modules/notifications';
import { vrmConfig } from '@/config';
import { AngularLocaleBundle } from '@/angular-bridge/modules/i18n';

@Component
@AngularDirective('vrm-remote-console2-modal')
@AngularLocaleBundle('core')
@AngularLocaleBundle('remoteconsole')
export default class VrmRemoteConsole2Modal extends Vue {
    @ngInject()
    ApiService;
    @ngInject()
    InstallationsService;
    @ngInject()
    UserService;
    @ngInject()
    $rootScope;
    @ngInject()
    vrmNotificationsService;
    @ngInject()
    $stateParams;
    @ngInject()
    locale;
    @ngInject()
    localStorageService;
    @Prop({ required: true })
    siteGuiHash;
    @Prop({ required: true })
    idSite;
    @Prop({ type: Boolean, required: true })
    isOpen;

    installation = null;
    remoteConsoleLoaded = false;
    addEventOverriden = false;
    isMobile = false;
    isTablet = false;

    EVENT_TYPES_TO_OVERRIDE = ['cut', 'copy', 'paste', 'input'];
    qtEventListeners = [];

    mounted() {
        const browser = Bowser.getParser(window.navigator.userAgent);
        this.isMobile = browser.parsedResult.platform.type === 'mobile';
        this.isTablet = browser.parsedResult.platform.type === 'tablet';

        this.prefetch();
    }

    async prefetch() {
        this.installation = await this.InstallationsService.getInstallation(this.idSite);

        if (this.installation.remoteConsoleChoice === 'rc_classic' || !this.canOpenRemoteConsole())
            return;
        const qtLoader = this.createQtLoader();
        document.querySelector('#qtstatus').innerHTML = this.downloadingText;

        qtLoader.fetchThenCompileWasmWithCredentials(
            this.ApiService,
            this.$stateParams,
            this.siteGuiHash,
            this.locale,
        );
    }

    get downloadingText() {
        return this.$t('core.rc_downloading', {
            i18nFallback: 'Downloading UI...',
        });
    }

    @Watch('isOpen')
    onModalToggle() {
        if (this.isOpen) {
            this.open();

            this.changePageVisibilityTimeout = setTimeout(() => {
                this.$rootScope.$broadcast('page-visibility-change', false);
                this.changePageVisibilityTimeout = null;
            }, 60000);
        } else if (this.changePageVisibilityTimeout) {
            clearTimeout(this.changePageVisibilityTimeout);
        } else {
            this.$rootScope.$broadcast('page-visibility-change', true);
        }
    }

    @Watch('siteGuiHash')
    @Watch('idSite')
    resetConsole() {
        const canvas = document.querySelector('#qtcanvas');
        canvas.innerHTML = '';
        canvas.remove();
        const newCanvas = document.createElement('div');
        newCanvas.id = 'qtcanvas';
        newCanvas.style.display = 'none';
        document.querySelector('#qtwrapper').appendChild(newCanvas);
        document.querySelector('#qtspinner').style.display = 'flex';
        this.handleClose();
        this.qtEventListeners = [];
        this.remoteConsoleLoaded = false;

        this.prefetch();
    }

    handleClose() {
        document.body.style.overflow = 'auto';
        window.removeEventListener('orientationchange', this.resizeTimeout);
        this.removeQtListeners();
        this.$emit('close');
    }

    handleOutsideClick(e) {
        if (this.isOpen && !this.$refs.modal.contains(e.target)) {
            this.handleClose();
        }
    }

    // Initialization function
    async open() {
        window.watchdogHit = false;
        window.guiv2initialized = false;
        document.body.style.overflow = 'hidden';
        if (!this.remoteConsoleLoaded) {
            await this.loadRemoteConsole();
        } else {
            this.addEventListeners();
            this.readdQtEventListeners();
            // dispatching a resize is needed to force the console to show on some android devices
            this.resizeTimeout();
        }

        if (!this.$rootScope.userState.isDemo) {
            // Logging the event that remote console has been accessed
            this.ApiService.setOpenedRemoteConsoleVersion(this.$stateParams.id, 2);
        }
    }

    async loadRemoteConsole() {
        this.installation = await this.InstallationsService.getInstallation(this.idSite);
        if (!this.canOpenRemoteConsole()) return;
        this.startConsole();
    }

    canOpenRemoteConsole() {
        if (!this.installation.view_permissions.share && !this.$rootScope.userState.isDemo) {
            this.vrmNotificationsService.add({
                type: NOTIFICATION_TYPE_ERROR,
                message: this.$t('core.not_allowed_shared_user', {
                    i18nFallback: 'You are not allowed to view this page as a shared user!',
                }),
            });
            return false;
        }
        if (!this.installation.view_permissions.rc_gui_v2) {
            this.vrmNotificationsService.add({
                type: NOTIFICATION_TYPE_ERROR,
                message: this.$t('core.not_supporting_new_rc', {
                    i18nFallback: 'This gateway does not support the new remote console!',
                }),
            });
            return false;
        }
        return true;
    }

    hideAfterEnter(e) {
        if (e.key !== 'Enter') return;
        if (e.target.parentElement === document.body && e.target.tagName === 'INPUT') {
            e.target.blur();
        }
    }

    overrideAddEvent() {
        this.addEventOverriden = true;
        EventTarget.prototype._addEventListener = EventTarget.prototype.addEventListener;

        const vueObj = this;
        EventTarget.prototype.addEventListener = function(eventType, eventFunction, eventOptions) {
            // Original addEventListener functionality
            let _eventOptions = eventOptions === undefined ? false : eventOptions;
            this._addEventListener(eventType, eventFunction, _eventOptions);

            if (vueObj.EVENT_TYPES_TO_OVERRIDE.includes(eventType)) {
                const listener = {
                    target: this,
                    type: eventType,
                    function: eventFunction,
                    options: _eventOptions,
                };
                vueObj.qtEventListeners.push(listener);
            }
        };
    }

    removeQtListeners() {
        document.removeEventListener('keydown', this.hideAfterEnter);

        if (this.addEventOverriden) {
            // Remove addEventListener override
            EventTarget.prototype.addEventListener = EventTarget.prototype._addEventListener;
            EventTarget.prototype._addEventListener = undefined;
            this.addEventOverriden = false;
        }

        for (const listener of this.qtEventListeners) {
            listener.target.removeEventListener(listener.type, listener.function, listener.options);
        }
    }

    resizeTimeout() {
        setTimeout(() => {
            window.dispatchEvent(new Event('resize'));
        }, 200);
    }

    showLoader(canvas, statusMessage) {
        return function(loaderStatus) {
            canvas.style.display = 'none';
            statusMessage.innerHTML = loaderStatus;
        };
    }

    showError(errorMessage, loading, error, canvas) {
        return function(errorText) {
            console.log(errorText);
            // error text is the actual error we want to set custom errors
            // display error in error and not in spinner
            errorMessage.innerHTML = errorText;
            loading.style.display = 'none';
            error.style.display = 'block';
            canvas.style.display = 'none';
        };
    }

    showExit(statusMessage, qtLoader) {
        const modal = this;
        return function() {
            statusMessage.innerHTML = 'Application exit';
            if (qtLoader.exitCode !== undefined)
                statusMessage.innerHTML += ' with code ' + qtLoader.exitCode;
            if (qtLoader.exitText !== undefined)
                statusMessage.innerHTML += ' (' + qtLoader.exitText + ')';
            modal.resetConsole();
        };
    }

    showCanvas(canvas, spinner) {
        return function() {
            canvas.style.display = 'block';
            canvas.style.height = '100%';
            canvas.style.width = '100%';
            spinner.style.display = 'none';
        };
    }
    get QtLoaderArguments() {
        const user = this.UserService.user;
        return [
            '--mqtt',
            'wss://' + this.installation.mqtt_webhost + '/mqtt?idUser=' + user.id,
            '--id',
            this.installation.identifier,
            '--user',
            `vrmlogin_${vrmConfig.mqttApiEnvironment}_${user.email}`,
            '--pass',
            new URLSearchParams(window.location.search).has('demoShare')
                ? vrmConfig.demoShareToken
                : this.localStorageService.get('token'),
        ];
    }

    createQtLoader() {
        const spinner = document.querySelector('#qtspinner');
        const canvas = document.querySelector('#qtcanvas');
        const statusMessage = document.querySelector('#qtstatus');
        const errorMessage = document.querySelector('#errstatus');
        const error = document.querySelector('#rc2error');
        const loading = document.querySelector('#rc2loading');
        const modal = this;
        return new _QtLoader({
            canvasElements: [canvas],
            restartLimit: 5,
            restartMode: 'RestartOnExit',
            showLoader: modal.showLoader(canvas, statusMessage),
            showError: modal.showError(errorMessage, loading, error, canvas),
            showExit: modal.showExit(statusMessage, this),
            showCanvas: modal.showCanvas(canvas, spinner),
            arguments: this.QtLoaderArguments,
        });
    }

    loadEmscriptenModule(qtLoader) {
        qtLoader
            .loadEmscriptenModule(
                'venus-gui-v2-loader',
                this.ApiService,
                this.$stateParams,
                this.siteGuiHash,
                true,
                this.locale,
            )
            .then(loaded => {
                if (!loaded) this.remoteConsoleLoaded = false;
            });
        this.remoteConsoleLoaded = true;
    }

    startConsole() {
        const qtLoader = this.createQtLoader();
        document.querySelector('#qtstatus').innerHTML = this.downloadingText;
        this.addEventListeners();
        this.loadEmscriptenModule(qtLoader);
    }

    addEventListeners() {
        window.addEventListener('orientationchange', this.resizeTimeout);
        if (!this.addEventOverriden) {
            this.overrideAddEvent();
        }
        if (this.isMobile || this.isTablet) {
            document.addEventListener('keydown', this.hideAfterEnter);
        }
    }

    readdQtEventListeners() {
        for (const listener of this.qtEventListeners) {
            listener.target._addEventListener(listener.type, listener.function, listener.options);
        }
    }
}
</script>
